import { useCallback, useRef, useState } from 'react';
import styled from 'styled-components';

import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AsyncSelect from 'react-select/async';
import type { GroupBase, StylesConfig, CSSObjectWithLabel } from 'react-select';

import { Controller, useForm, FormProvider } from 'react-hook-form';

import {
    OdooPartnerKind,
    useCreateOrganizationMutation,
    useSearchCompaniesByNameLazyQuery,
    TotadminPermission,
} from 'data/__generated__';
import { PAGES } from 'constants/pages';
import { isValidSiret, normalizeSiret } from 'utils/siret';

import { Header, HeaderTitle } from 'components/Header';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';
import { PageTitle } from 'components/PageTitle';
import { DetailFormValue } from 'components/DetailsView';
import { TotemLabel } from 'components/TotemLabel';
import { Loader, LoaderModeType } from 'components/Loader';
import { OdooPartnerSelect } from 'components/OdooPartnerSelect';

type FormData = {
    name: string;
    domain?: string;
    siret: string;
    // Société Odoo choisie explicitement : rattache l'organisation à une fiche EXISTANTE au lieu d'en
    // créer une. Vide = l'API crée la société dans Odoo à la création de l'organisation.
    odooOrganizationId: number | null;
};

// Option enrichie : react-select nous restitue l'objet complet au `onChange`
// (on récupère ainsi le SIRET du siège + la raison sociale, pas seulement une `value`).
type CompanyOption = {
    label: string;
    value: string;
    legalName: string;
    siret: string;
    isActive: boolean;
};

const MIN_SEARCH_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_LIMIT = 10;

export const OrganizationCreate = () => {
    const methods = useForm<FormData>({
        defaultValues: { name: '', domain: '', siret: '', odooOrganizationId: null },
    });
    const navigate = useNavigate();
    const [createOrganization, { loading }] = useCreateOrganizationMutation();
    const [searchCompanies] = useSearchCompaniesByNameLazyQuery();
    const { control, handleSubmit, register, setValue, getValues, watch } = methods;

    const [selectedCompany, setSelectedCompany] = useState<CompanyOption | null>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout>>();

    const siret = watch('siret');

    // Recherche Sirene par raison sociale (débounce léger — API publique). Chaque option porte le
    // SIRET de son siège → c'est ce qui identifiera le SIRET de l'organisation.
    const loadCompanyOptions = useCallback(
        (input: string): Promise<CompanyOption[]> =>
            new Promise((resolve) => {
                if (debounceRef.current) {
                    clearTimeout(debounceRef.current);
                }
                const query = input.trim();
                if (query.length < MIN_SEARCH_LENGTH) {
                    resolve([]);
                    return;
                }
                debounceRef.current = setTimeout(async () => {
                    const { data } = await searchCompanies({ variables: { name: query, limit: SEARCH_LIMIT } });
                    resolve(
                        (data?.searchCompaniesByName ?? []).map((company) => ({
                            value: company.siret,
                            siret: company.siret,
                            legalName: company.legalName,
                            isActive: company.isActive,
                            label:
                                [company.legalName, company.address].filter(Boolean).join(' — ') +
                                (company.isActive ? '' : ' — (fermée)'),
                        })),
                    );
                }, SEARCH_DEBOUNCE_MS);
            }),
        [searchCompanies],
    );

    // À la sélection : identifie le SIRET (siège) et pré-remplit le nom s'il est encore vide.
    const onSelectCompany = (option: CompanyOption | null) => {
        setSelectedCompany(option);
        setValue('siret', option?.siret ?? '', { shouldValidate: true, shouldDirty: true });
        if (option && !getValues('name')) {
            setValue('name', option.legalName, { shouldValidate: true, shouldDirty: true });
        }
    };

    const onSubmit = handleSubmit(async (fields) => {
        const { name, domain, siret: submittedSiret, odooOrganizationId } = fields;

        // Garde-fou : le SIRET provient d'une sélection Sirene, mais on revalide à la soumission.
        if (!isValidSiret(submittedSiret)) {
            toast.error('Sélectionnez une entreprise pour identifier son SIRET.');
            return;
        }

        const { data } = await createOrganization({
            variables: { input: { name, domain, siret: normalizeSiret(submittedSiret), odooOrganizationId } },
        });
        if (data) {
            const {
                createOrganization: { error, createdOrganization },
            } = data;
            if (createdOrganization) {
                toast.success(`L'organisation ${name} a bien été créé`);
                navigate(`/organization/${createdOrganization._id}`);
            } else if (error) {
                toast.error(error);
            } else {
                throw Error("Une erreur inconnue s'est produite");
            }
        } else {
            throw Error("Une erreur inconnue s'est produite");
        }
    });

    const selectStyles: StylesConfig<CompanyOption, false, GroupBase<CompanyOption>> = {
        control: (base: CSSObjectWithLabel) => ({ ...base, minHeight: 45 }),
    };

    return (
        <Container>
            <FormProvider {...methods}>
                <Form onSubmit={onSubmit}>
                    <Header>
                        <HeaderTitle>
                            <PageTitle page={PAGES.organizationCreate} />
                        </HeaderTitle>
                        <Link to="/organizations">
                            <TotemPrimaryButton
                                $isSecondaryStyle
                                requiredPermissions={[TotadminPermission.OrganizationsRead]}
                            >
                                Retour
                            </TotemPrimaryButton>
                        </Link>
                    </Header>
                    <Content>
                        <ScrollableContent>
                            <FieldContainer data-test="organization-create_company">
                                <LabelBlock>
                                    <TotemLabel>Raison sociale</TotemLabel>
                                    <Sublabel>
                                        Recherchez l'entreprise pour identifier automatiquement son SIRET
                                    </Sublabel>
                                </LabelBlock>
                                <div className="text-black relative">
                                    <AsyncSelect<CompanyOption, false>
                                        placeholder="Rechercher une raison sociale…"
                                        cacheOptions
                                        value={selectedCompany}
                                        onChange={onSelectCompany}
                                        loadOptions={loadCompanyOptions}
                                        loadingMessage={() => 'Recherche…'}
                                        noOptionsMessage={({ inputValue }) =>
                                            inputValue.trim().length < MIN_SEARCH_LENGTH
                                                ? 'Saisissez au moins 3 caractères'
                                                : 'Aucune entreprise trouvée'
                                        }
                                        isClearable
                                        styles={selectStyles}
                                    />
                                </div>
                            </FieldContainer>
                            <DetailFormValue
                                label="SIRET"
                                sublabel="Identifié automatiquement depuis la raison sociale"
                                {...register('siret', { required: true, validate: isValidSiret })}
                                readOnly
                                className="cursor-not-allowed opacity-60"
                                placeholder="—"
                                data-test="organization-create_siret"
                            />
                            <DetailFormValue
                                label="Nom de l'organisation"
                                {...register('name', { required: true })}
                                data-test="organization-create_name"
                            />
                            <DetailFormValue
                                label="domaine de l'organisation (optionnel)"
                                {...register('domain', { required: false })}
                                data-test="organization-create_domain"
                            />
                            {/* Rattachement à une société Odoo existante. Laissé vide, l'API crée la
                                société dans Odoo — c'est le cas courant. Le champ sert aux clients déjà
                                connus de la compta (facturés hors Totem, repris d'i-Lunch), pour ne pas
                                en créer un doublon. */}
                            <Controller
                                name="odooOrganizationId"
                                control={control}
                                render={({ field }) => (
                                    <OdooPartnerSelect
                                        kind={OdooPartnerKind.Customer}
                                        label="Société Odoo (optionnel)"
                                        sublabel="Rattache l'organisation à une société Odoo existante. Vide = la société est créée dans Odoo."
                                        value={field.value ?? null}
                                        onChange={(option) => field.onChange(option?.value ?? null)}
                                        dataTest="organization-create_odooOrganizationId"
                                    />
                                )}
                            />
                            <ButtonContainer>
                                <TotemPrimaryButton
                                    $minWidth="140px"
                                    type="submit"
                                    disabled={!isValidSiret(siret)}
                                    data-test="submit-button"
                                >
                                    {loading ? (
                                        <Loader size="18px" mode={LoaderModeType.Spin} />
                                    ) : (
                                        <>Créer une organisation</>
                                    )}
                                </TotemPrimaryButton>
                            </ButtonContainer>
                        </ScrollableContent>
                    </Content>
                </Form>
            </FormProvider>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
`;

const Content = styled.div`
    flex: 1;
    overflow: hidden;
`;
const ScrollableContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 15px;
    padding: 15px;
    width: 100%;
    height: 100%;
    overflow-y: auto;
`;

const FieldContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;
    color: ${({ theme }) => theme.textColor};
`;

const LabelBlock = styled.div`
    display: flex;
    flex-direction: column;
`;

const Sublabel = styled.span`
    color: ${({ theme }) => theme.infoTextColor};
    font-size: 13px;
`;

const ButtonContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: 15px;
    background-color: ${({ theme }) => theme.backgroundColor};
`;
